import React, { useEffect, useState } from "react";
import { LoadingIcon, EditLargeIcon } from "../../assets/icons/Icons";
import FormSection from "../form/Section";
import FormHeader from "../form/Header";
import Switch from "../form/Switch";
import Popup from "../Popup";
import { GetRole } from "../../api/roles/getRole";
import { UpdateRole } from "../../api/roles/updateRole";
import { ToggleRoleActive } from "../../api/roles/toggleRoleActive";
import { GetPermissions } from "../../api/roles/getPermissions";
import PermissionMatrix from "./PermissionMatrix";
import {
  buildPermissionGroups,
  parseRolePermissions,
  serializePermissions,
  hasFeaturePermission,
  isReservedRoleName,
  OFFICE_ACCESS_KEY,
} from "./permissionCatalog";
import { useTranslation } from "react-i18next";

const EditRoleForm = ({ role, onUpdate, onClose }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [name, setName] = useState(role.name || "");
  const [description, setDescription] = useState(role.description || "");
  const [active, setActive] = useState(role.is_active);
  const [initialActive, setInitialActive] = useState(role.is_active);

  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({});
  const [officeAccessAvailable, setOfficeAccessAvailable] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [catalogRes, detail] = await Promise.all([
          GetPermissions(),
          GetRole(role.id),
        ]);
        const catalogMap = catalogRes.permissions || {};
        setGroups(buildPermissionGroups(catalogMap));
        setOfficeAccessAvailable(!!catalogMap[OFFICE_ACCESS_KEY]);
        setName(detail.name || "");
        setDescription(detail.description || "");
        setActive(detail.is_active);
        setInitialActive(detail.is_active);
        setSelected(parseRolePermissions(detail.permissions));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingData(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  // Office scope is a role-wide flag, not a feature: on = all offices, off =
  // limited to the offices assigned to each user. Wired to the all_offices
  // grant so it serializes alongside the matrix permissions.
  const officeAccessAll = !!selected[OFFICE_ACCESS_KEY]?.read;
  const setOfficeAccessAll = (checked) =>
    setSelected({
      ...selected,
      [OFFICE_ACCESS_KEY]: { read: checked, write: false },
    });

  // A role must have a name and grant at least one feature permission; the
  // office-scope flag alone does not make a usable role.
  const featureSelected = hasFeaturePermission(selected);
  const nameReserved = isReservedRoleName(name);

  useEffect(() => {
    setSubmitDisabled(!name.trim() || !featureSelected || nameReserved);
  }, [name, featureSelected, nameReserved]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitDisabled(true);
    setError("");
    try {
      await UpdateRole(role.id, {
        name,
        description,
        permissions: serializePermissions(selected),
      });
      if (active !== initialActive) {
        await ToggleRoleActive(role.id);
      }
      setLoading(false);
      onUpdate();
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSubmitDisabled(false);
    }
  };

  return (
    <Popup size="large" withClose withOverlay onClose={onClose}>
      <form method="post" onSubmit={handleSubmit}>
        <FormHeader
          title={t("Edit Role")}
          description={t("Edit this role's details and permissions.")}
          icon={<EditLargeIcon />}
        >
          <p className="text-right mt-4">
            <strong>{t("Role ID")}: </strong>
            {role.id}
          </p>
        </FormHeader>

        <div className="row mb-5 mt-5">
          <div className="col-xs-6">
            <FormSection title={t("Role Name")} required>
              <input
                type="text"
                name="name"
                id="name"
                maxLength="255"
                placeholder={t("Role name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {nameReserved && (
                <p className="color-error mt-2">
                  {t(
                    "This name is reserved for a built-in role. Choose a different name."
                  )}
                </p>
              )}
            </FormSection>
          </div>
          <div className="col-xs-6">
            <FormSection title={t("Active")}>
              <Switch title={t("Active")}>
                <input
                  type="checkbox"
                  name="active"
                  id="active"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
              </Switch>
            </FormSection>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-xs-12">
            <FormSection title={t("Description")}>
              <input
                type="text"
                name="description"
                id="description"
                maxLength="500"
                placeholder={t("Short description of this role")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormSection>
          </div>
        </div>

        {officeAccessAvailable && (
          <div className="row mb-5">
            <div className="col-xs-12">
              <FormSection title={t("Office Access")}>
                <Switch title={t("This role can access all offices")}>
                  <input
                    type="checkbox"
                    name="all_offices"
                    id="all_offices"
                    checked={officeAccessAll}
                    onChange={(e) => setOfficeAccessAll(e.target.checked)}
                  />
                </Switch>
                <p
                  className="text-muted mt-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  {t(
                    "When off, users with this role are limited to the offices assigned to them."
                  )}
                </p>
              </FormSection>
            </div>
          </div>
        )}

        <div className="row mb-5">
          <div className="col-xs-12">
            <FormSection title={t("Permissions")}>
              {loadingData ? (
                <p>{t("Loading permissions...")}</p>
              ) : (
                <PermissionMatrix
                  groups={groups}
                  selected={selected}
                  onChange={setSelected}
                  disabled={loading}
                />
              )}
              {!loadingData && !featureSelected && (
                <p className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                  {t("Select at least one permission to save this role.")}
                </p>
              )}
            </FormSection>
          </div>
        </div>

        <div className="text-right">
          <button
            className={`btn btn-primary btn-medium ${loading ? "btn-loading" : ""}`}
            type="submit"
            name="submit"
            disabled={submitDisabled}
          >
            {loading ? <LoadingIcon /> : ""}
            {t("Save")}
          </button>
        </div>
        {error && <p className="color-error text-center mt-3">{error}</p>}
      </form>
    </Popup>
  );
};

export default EditRoleForm;
